import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bot, Check, FileKey, Loader2, Pencil, Plus, Server, Trash2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { AgentAvatar } from "@/components/agent-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError, type RemoteMachinePayload } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { qk } from "@/lib/query/keys";
import type { AgentWithContext, RemoteMachine, RemoteMachinesView } from "@/lib/types";

const selectAgents = (data: { agents: AgentWithContext[] }) => data.agents;
const NO_AGENTS: AgentWithContext[] = [];

/** Every agent, from the shared cache (Settings → Remote machines is admin-only, as the list is). */
function useAgentList(): AgentWithContext[] {
  return useQuery({ queryKey: qk.agents(), queryFn: api.listAgents, select: selectAgents }).data ?? NO_AGENTS;
}

/**
 * Settings → Remote machines (admins): SSH hosts agents can run commands on. The
 * host worker writes each into `~/.ssh/board-remotes/` so `ssh <name>` and the host's
 * `remote` CLI work from any agent. The private key is write-only: pasted or loaded
 * here, sealed by the API to the worker's key, never shown again — the form only says
 * that one is stored and lets you replace it.
 */
export function RemoteMachinesSettings() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.remoteMachines(), queryFn: api.listRemoteMachines, refetchInterval: 5_000 });
  const agents = useAgentList();
  const [editing, setEditing] = useState<RemoteMachine | "new" | null>(null);
  const [deleting, setDeleting] = useState<RemoteMachine | null>(null);
  const refresh = () => void client.invalidateQueries({ queryKey: qk.remoteMachines() });

  if (query.isPending) return <p className="text-[13px] text-muted-foreground">Loading…</p>;
  if (query.isError) return <p className="text-[13px] text-destructive">Could not load the remote machines.</p>;
  const view = query.data;

  return (
    <div className="space-y-3">
      <HostLine host={view.host} />
      {view.machines.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          None yet. Add one with its IP, login and the .pem key; agents then reach it as <code className="rounded bg-muted px-1">ssh &lt;name&gt;</code>.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {view.machines.map((machine) => (
            <MachineRow key={machine.id} machine={machine} agents={agents} onEdit={() => setEditing(machine)} onDelete={() => setDeleting(machine)} />
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onClick={() => setEditing("new")} disabled={!view.host}>
          <Plus /> Add machine
        </Button>
        {view.machines.length > 0 ? (
          <span className="text-[12.5px] text-muted-foreground">
            Agents run <code className="rounded bg-muted px-1">remote run {view.machines[0]!.name} "sudo whoami"</code>
          </span>
        ) : null}
      </div>
      <MachineDialog
        machine={editing}
        agents={agents}
        reserved={view.host?.reservedNames ?? []}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />
      <DeleteDialog
        machine={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          refresh();
        }}
      />
    </div>
  );
}

function HostLine({ host }: { host: RemoteMachinesView["host"] }) {
  if (!host) {
    return (
      <p className="flex items-start gap-1.5 text-[12.5px] text-[var(--waiting)]">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        The worker on the host has not reported yet. Machines can be added once it has, since keys are sealed to it.
      </p>
    );
  }
  if (host.stale) {
    return (
      <p className="flex items-start gap-1.5 text-[12.5px] text-[var(--waiting)]">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        The host worker last synced {relativeTime(host.observedAt)}; changes reach the host when it is back.
      </p>
    );
  }
  return (
    <p className="text-[12.5px] text-muted-foreground [overflow-wrap:anywhere]">
      Written to <code className="rounded bg-muted px-1">{host.dir}</code> on the host
      {host.syncedAt ? `, last checked ${relativeTime(host.syncedAt)}` : ""}. Keys are synced to the host for agents to use; they are never shown here
      again. Which agents may use a machine is advisory: every agent runs as the host user, so it prevents mistakes, not a determined agent.
    </p>
  );
}

function syncState(machine: RemoteMachine): { label: string; tone: "ok" | "wait" | "bad"; detail?: string } {
  if (!machine.sync) return { label: "Waiting for the host", tone: "wait" };
  if (!machine.sync.ok) return { label: "Not on the host", tone: "bad", detail: machine.sync.error ?? undefined };
  if (machine.sync.updatedAt < machine.updatedAt) return { label: "Syncing…", tone: "wait" };
  return { label: "On the host", tone: "ok" };
}

/** "All agents", or the allowed agents by name (an agent deleted since shows as "a deleted agent"). */
function allowedLabel(machine: RemoteMachine, agents: AgentWithContext[]): string {
  if (machine.agents === null) return "All agents";
  const names = machine.agents.map((id) => {
    const agent = agents.find((a) => a.id === id);
    return agent ? `@${agent.handle}` : "a deleted agent";
  });
  return `Only ${names.join(", ")}`;
}

function MachineRow({ machine, agents, onEdit, onDelete }: { machine: RemoteMachine; agents: AgentWithContext[]; onEdit: () => void; onDelete: () => void }) {
  const state = syncState(machine);
  return (
    <li className="flex flex-col gap-2 px-3 py-2.5 win-sm:flex-row win-sm:items-start">
      <Server className="mt-0.5 hidden size-4 shrink-0 text-muted-foreground win-sm:block" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-mono text-[13.5px] font-semibold">{machine.name}</span>
          <span className="font-mono text-[12.5px] text-muted-foreground [overflow-wrap:anywhere]">
            {machine.user}@{machine.host}
            {machine.port === 22 ? "" : `:${machine.port}`}
          </span>
          {state.tone === "ok" ? (
            <Badge tint="var(--kind-done)">
              <Check className="size-3" /> {state.label}
            </Badge>
          ) : state.tone === "bad" ? (
            <Badge tint="var(--destructive)">
              <AlertTriangle className="size-3" /> {state.label}
            </Badge>
          ) : (
            <Badge variant="secondary">{state.label}</Badge>
          )}
        </div>
        {state.detail ? <p className="text-[12.5px] text-destructive">{state.detail}</p> : null}
        <p className="flex items-start gap-1 text-[12.5px] text-muted-foreground [overflow-wrap:anywhere]">
          <Bot className="mt-0.5 size-3.5 shrink-0" />
          {allowedLabel(machine, agents)}
        </p>
        {machine.notes ? <p className="whitespace-pre-wrap text-[12.5px] text-muted-foreground [overflow-wrap:anywhere]">{machine.notes}</p> : null}
        <p className="text-[11.5px] text-muted-foreground">
          {machine.keyKind ? `${machine.keyKind} key` : "Key"} stored {relativeTime(machine.keyUpdatedAt)}
          {machine.sync?.fingerprint ? (
            <>
              {" · "}
              <span className="font-mono [overflow-wrap:anywhere]">{machine.sync.fingerprint}</span>
            </>
          ) : null}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button size="sm" variant="ghost" onClick={onEdit} aria-label={`Edit ${machine.name}`}>
          <Pencil /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} aria-label={`Delete ${machine.name}`} className="text-destructive hover:text-destructive">
          <Trash2 /> Delete
        </Button>
      </div>
    </li>
  );
}

const blank = { name: "", host: "", port: "22", user: "ubuntu", notes: "" };

function MachineDialog({
  machine,
  agents,
  reserved,
  onClose,
  onSaved,
}: {
  machine: RemoteMachine | "new" | null;
  agents: AgentWithContext[];
  reserved: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = machine && machine !== "new" ? machine : null;
  const [form, setForm] = useState(blank);
  const [key, setKey] = useState<{ text: string; source: string } | null>(null);
  const [replacing, setReplacing] = useState(false);
  /** null: every agent. */
  const [allowed, setAllowed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openFor, setOpenFor] = useState<RemoteMachine | "new" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Reset when the dialog opens for another machine (or a new one).
  if (machine !== openFor) {
    setOpenFor(machine);
    setForm(existing ? { name: existing.name, host: existing.host, port: String(existing.port), user: existing.user, notes: existing.notes ?? "" } : blank);
    setKey(null);
    setReplacing(false);
    setAllowed(existing?.agents ?? null);
    setError(null);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: RemoteMachinePayload = {
        name: form.name.trim(),
        host: form.host.trim(),
        port: Number(form.port) || 22,
        user: form.user.trim(),
        notes: form.notes.trim() || null,
        agents: allowed,
        ...(key ? { privateKey: key.text } : {}),
      };
      return existing ? api.updateRemoteMachine(existing.id, payload) : api.createRemoteMachine(payload);
    },
    onSuccess: (saved) => {
      toast.success(existing ? `Saved ${saved.name}` : `Added ${saved.name}; the host has it within a few seconds`);
      setKey(null);
      onSaved();
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : "Could not save the machine"),
  });

  const name = form.name.trim().toLowerCase();
  const nameProblem = name && !/^[a-z0-9][a-z0-9-]{0,31}$/.test(name)
    ? "Lower-case letters, digits and dashes (it is the SSH alias)."
    : reserved.includes(name) && name !== existing?.name
      ? "Already a Host alias in the host's ~/.ssh/config."
      : null;
  const needsKey = !existing || replacing;
  const ready = name && !nameProblem && form.host.trim() && form.user.trim() && (!needsKey || key) && (allowed === null || allowed.length > 0);

  const loadFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 16 * 1024) {
      setError("That file is too large for an SSH private key.");
      return;
    }
    setKey({ text: await file.text(), source: file.name });
    setError(null);
  };

  return (
    <Dialog open={machine !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg [&>*]:min-w-0" onOpenAutoFocus={(event) => existing && event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.name}` : "Add a remote machine"}</DialogTitle>
          <DialogDescription>Agents reach it from the host as <code className="rounded bg-muted px-1">ssh {name || "<name>"}</code>, with full sudo if the login has it.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready && !save.isPending) save.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Name" hint={nameProblem ?? "The SSH alias, e.g. demo-ec2"} bad={Boolean(nameProblem)}>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="demo-ec2" autoCapitalize="none" spellCheck={false} />
            </Field>
            <Field label="Host or IP">
              <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="3.120.45.6" autoCapitalize="none" spellCheck={false} />
            </Field>
            <Field label="User" hint="ubuntu on Ubuntu AMIs, ec2-user on Amazon Linux">
              <Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} autoCapitalize="none" spellCheck={false} />
            </Field>
            <Field label="Port">
              <Input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value.replace(/\D/g, "") })} inputMode="numeric" />
            </Field>
          </div>

          <div className="space-y-1.5">
            <Label>Private key</Label>
            {!needsKey ? (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-[12.5px]">
                <FileKey className="size-4 text-muted-foreground" />
                <span className="flex-1">
                  {existing!.keyKind ? `${existing!.keyKind} key` : "A key"} is stored ({relativeTime(existing!.keyUpdatedAt)}). It is never shown.
                </span>
                <Button type="button" size="sm" variant="outline" onClick={() => setReplacing(true)}>
                  Replace key
                </Button>
              </div>
            ) : key ? (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-[12.5px]">
                <FileKey className="size-4 text-[var(--kind-done)]" />
                <span className="flex-1">
                  {key.source} ready ({key.text.length.toLocaleString()} characters). Saved encrypted, never shown again.
                </span>
                <Button type="button" size="sm" variant="ghost" onClick={() => setKey(null)} aria-label="Clear the key">
                  <X /> Clear
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Textarea
                  rows={3}
                  className="font-mono text-[12px]"
                  placeholder="Paste the .pem here, or load the file"
                  value=""
                  onChange={(e) => e.target.value.trim() && setKey({ text: e.target.value, source: "Pasted key" })}
                  autoCapitalize="none"
                  spellCheck={false}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                    <Upload /> Load .pem file
                  </Button>
                  {existing ? (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setReplacing(false)}>
                      Keep the stored key
                    </Button>
                  ) : null}
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      void loadFile(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <AllowedAgents agents={agents} allowed={allowed} onChange={setAllowed} />

          <Field label="Notes" hint="What it is for; agents see this in remote_list">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Demo for Acme, Ubuntu 24.04, t3.small" />
          </Field>

          {error ? <p className="break-words text-xs text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready || save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : null} {existing ? "Save" : "Add machine"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Agents allowed": every agent (the default), or only the ticked ones. Advisory — the
 * board hides the machine from the others' remote_list and the host's remote CLI refuses
 * it for them, but every agent is the same host user.
 */
function AllowedAgents({ agents, allowed, onChange }: { agents: AgentWithContext[]; allowed: string[] | null; onChange: (next: string[] | null) => void }) {
  // Those allowed when the dialog opened lead the list, so a saved choice is in view;
  // fixed at open, so a row does not jump away from the finger that ticks it.
  const [initial] = useState(() => new Set(allowed ?? []));
  const ordered = [...agents].sort((a, b) => Number(initial.has(b.id)) - Number(initial.has(a.id)));
  const known = new Set(agents.map((a) => a.id));
  // Ids of agents deleted since stay ticked (the list never widens by itself) but have no row.
  const gone = (allowed ?? []).filter((id) => !known.has(id));
  const toggle = (id: string) => {
    const list = allowed ?? [];
    onChange(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };
  return (
    <div className="space-y-1.5">
      <Label>Agents allowed</Label>
      <div className="flex flex-col gap-1.5 text-[13px] sm:flex-row sm:gap-4">
        <label className="flex cursor-pointer items-center gap-2">
          <input type="radio" name="remote-agents" className="size-4 accent-[var(--primary)]" checked={allowed === null} onChange={() => onChange(null)} />
          All agents
        </label>
        <label className="flex cursor-pointer items-center gap-2">
          <input type="radio" name="remote-agents" className="size-4 accent-[var(--primary)]" checked={allowed !== null} onChange={() => onChange(allowed ?? [])} />
          Only these agents
          {allowed !== null && allowed.length > 0 ? <span className="text-muted-foreground">· {allowed.length} of {agents.length} ticked</span> : null}
        </label>
      </div>
      {allowed !== null ? (
        <>
          <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-border p-1">
            {ordered.map((agent) => (
              <li key={agent.id}>
                <label className="flex cursor-pointer items-center gap-2.5 rounded px-1.5 py-1 hover:bg-muted/40">
                  <input type="checkbox" className="size-4 accent-[var(--primary)]" checked={allowed.includes(agent.id)} onChange={() => toggle(agent.id)} />
                  <AgentAvatar agent={agent} size="sm" />
                  <span className="min-w-0 flex-1 truncate">
                    {agent.name} <span className="text-muted-foreground">@{agent.handle}</span>
                  </span>
                </label>
              </li>
            ))}
            {gone.map((id) => (
              <li key={id}>
                <label className="flex cursor-pointer items-center gap-2.5 rounded px-1.5 py-1 text-muted-foreground hover:bg-muted/40">
                  <input type="checkbox" className="size-4 accent-[var(--primary)]" checked onChange={() => toggle(id)} />
                  A deleted agent <span className="font-mono text-[11.5px]">{id}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className={`text-[11.5px] ${allowed.length === 0 ? "text-destructive" : "text-muted-foreground"}`}>
            {allowed.length === 0
              ? "Tick at least one agent, or allow all agents."
              : "Other agents do not see it in remote_list, and the host's remote CLI refuses it for them. Advisory: every agent runs as the host user."}
          </p>
        </>
      ) : null}
    </div>
  );
}

function Field({ label, hint, bad, children }: { label: string; hint?: string; bad?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint ? <p className={`text-[11.5px] ${bad ? "text-destructive" : "text-muted-foreground"}`}>{hint}</p> : null}
    </div>
  );
}

function DeleteDialog({ machine, onClose, onDeleted }: { machine: RemoteMachine | null; onClose: () => void; onDeleted: () => void }) {
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteRemoteMachine(id),
    onSuccess: (result) => {
      toast.success(`Deleted ${result.name}; its key leaves the host within a few seconds`);
      onDeleted();
    },
    onError: (cause) => toast.error(cause instanceof ApiError ? cause.message : "Could not delete the machine"),
  });
  return (
    <Dialog open={machine !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-4 shrink-0 text-destructive" /> Delete {machine?.name}?
          </DialogTitle>
          <DialogDescription>
            Its key and its SSH alias are removed from the host, and the stored key is gone with it. The machine itself is untouched; adding it
            again needs the .pem.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => machine && remove.mutate(machine.id)} disabled={remove.isPending}>
            <Trash2 /> {remove.isPending ? "Deleting…" : "Delete machine"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
